import React, { useState, useRef, useEffect, useCallback } from "react";
import { COLORS } from "../../../../theme/colors";
import Icon from "../../../../components/Icon";

// agent-experience-redesign-v1.md §5.2 + §7 (A6).
//
// Click-to-edit primitive used by every Detail Viewer field that the
// tenant rightfully owns. Reads as plain content at idle (so the page
// remains a dashboard, not a form) and morphs into the appropriate
// input on click. Save on blur or Cmd+Enter; Esc reverts. Optimistic
// update with rollback on 4xx/5xx propagated from `onSave`.
//
// Variants supported: text, textarea, number, select, slider, pill.
// Custom render is also possible via the `renderInput` prop for cases
// the built-in variants don't cover (tag editors, JSON editors, etc.).
//
// Usage:
//   <EditableField
//     label="Name" variant="text" value={agent.name}
//     onSave={async (v) => api.update(agent.id, { name: v })}
//     validate={(v) => v.trim() ? null : "Name is required"}
//   />
//
// Props:
//   variant       — "text" | "textarea" | "number" | "select" | "slider" | "pill" | "custom"
//   value         — current persisted value
//   onSave        — async (newValue) => void; throws on server-side rejection
//   validate      — (newValue) => string|null; client-side check; non-null blocks save
//   options       — for "select" / "pill" variants: [{ value, label }]
//   min,max,step  — for "number" / "slider" variants
//   placeholder   — for "text" / "textarea"
//   rows          — for "textarea" (default 4)
//   readOnly      — render as plain content with no edit affordance (for tree-shape fields, §5.4)
//   renderInput   — for "custom" variant: (value, setDraft, save, revert) => JSX
//   renderDisplay — override the read-state rendering (default: stringify value)

const PENCIL_OPACITY_HOVER = 0.9;
const PENCIL_OPACITY_IDLE = 0;

export default function EditableField({
  label,
  variant = "text",
  value,
  onSave,
  validate,
  options = [],
  min, max, step,
  placeholder,
  rows = 4,
  readOnly = false,
  renderInput,
  renderDisplay,
  style,
  inputStyle,
  hint,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hover, setHover] = useState(false);
  const inputRef = useRef(null);

  // Keep the draft in sync if the parent's value changes underneath us
  // (e.g. a refetch after another tab edited the same row).
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  // Auto-focus when entering edit mode.
  useEffect(() => {
    if (editing && inputRef.current) {
      try { inputRef.current.focus(); } catch {}
      // Select-all so re-editing the same field doesn't require dragging.
      if (typeof inputRef.current.select === "function") {
        try { inputRef.current.select(); } catch {}
      }
    }
  }, [editing]);

  const startEdit = useCallback(() => {
    if (readOnly) return;
    setDraft(value);
    setError(null);
    setEditing(true);
  }, [readOnly, value]);

  const revert = useCallback(() => {
    setDraft(value);
    setError(null);
    setEditing(false);
  }, [value]);

  const commit = useCallback(async () => {
    if (!editing) return;
    // Don't fire a save if the user didn't change anything.
    const same = JSON.stringify(draft) === JSON.stringify(value);
    if (same) {
      setEditing(false);
      return;
    }
    if (validate) {
      const e = validate(draft);
      if (e) {
        setError(e);
        return;
      }
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(draft);
      setEditing(false);
    } catch (err) {
      // Server-side rejection. Surface message and stay in edit mode so
      // the user can fix-and-resubmit without losing their draft.
      setError(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [editing, draft, value, validate, onSave]);

  const handleKey = useCallback((e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      revert();
    } else if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      commit();
    } else if (variant !== "textarea" && e.key === "Enter") {
      e.preventDefault();
      commit();
    }
  }, [variant, revert, commit]);

  // ── Read state ─────────────────────────────────────────
  if (!editing) {
    return (
      <div
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={startEdit}
        title={readOnly ? "Tree-shape edits live in the Builder — click 'Open in Builder' in the header." : undefined}
        style={{
          display: "flex", alignItems: "flex-start", gap: 8,
          cursor: readOnly ? "default" : "text",
          padding: "4px 0",
          ...style,
        }}
      >
        {label && (
          <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 600, minWidth: 100, paddingTop: 2 }}>
            {label}
          </div>
        )}
        <div style={{ flex: 1, fontSize: 13, color: COLORS.text, lineHeight: 1.5 }}>
          {renderDisplay ? renderDisplay(value) : (
            value === null || value === undefined || value === ""
              ? <span style={{ color: COLORS.textDim, fontStyle: "italic" }}>(empty)</span>
              : String(value)
          )}
        </div>
        {!readOnly && (
          <div style={{
            opacity: hover ? PENCIL_OPACITY_HOVER : PENCIL_OPACITY_IDLE,
            transition: "opacity 0.18s ease",
            paddingTop: 2,
          }}>
            <Icon name="action.edit" size={12} color={COLORS.textDim} />
          </div>
        )}
      </div>
    );
  }

  // ── Edit state ─────────────────────────────────────────
  const accent = error ? (COLORS.pink || "#ef4444") : COLORS.accent;
  const baseInputStyle = {
    fontSize: 13, color: COLORS.text,
    background: COLORS.surface,
    border: `1px solid ${accent}55`, borderRadius: 6,
    padding: "6px 10px", outline: "none",
    width: "100%", boxSizing: "border-box",
    boxShadow: `0 0 0 3px ${accent}10`,
    transition: "border-color 0.18s, box-shadow 0.18s",
    ...inputStyle,
  };

  let input = null;
  if (variant === "text") {
    input = (
      <input
        ref={inputRef}
        type="text"
        value={draft || ""}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKey}
        placeholder={placeholder}
        style={baseInputStyle}
      />
    );
  } else if (variant === "textarea") {
    input = (
      <textarea
        ref={inputRef}
        value={draft || ""}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKey}
        placeholder={placeholder}
        rows={rows}
        style={{ ...baseInputStyle, resize: "vertical", fontFamily: "inherit" }}
      />
    );
  } else if (variant === "number") {
    input = (
      <input
        ref={inputRef}
        type="number"
        value={draft ?? ""}
        min={min} max={max} step={step}
        onChange={(e) => setDraft(e.target.value === "" ? null : Number(e.target.value))}
        onBlur={commit}
        onKeyDown={handleKey}
        style={baseInputStyle}
      />
    );
  } else if (variant === "select") {
    input = (
      <select
        ref={inputRef}
        value={draft ?? ""}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKey}
        style={baseInputStyle}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label ?? o.value}</option>
        ))}
      </select>
    );
  } else if (variant === "slider") {
    input = (
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <input
          ref={inputRef}
          type="range"
          value={Number(draft ?? 0)}
          min={min} max={max} step={step}
          onChange={(e) => setDraft(Number(e.target.value))}
          onMouseUp={commit}
          onKeyDown={handleKey}
          style={{ flex: 1 }}
        />
        <div style={{ fontSize: 12, color: COLORS.text, minWidth: 40, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
          {Number(draft ?? 0).toFixed(step && step < 1 ? 2 : 0)}
        </div>
      </div>
    );
  } else if (variant === "pill") {
    input = (
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {options.map((o) => {
          const active = draft === o.value;
          return (
            <button
              key={o.value}
              type="button"
              onClick={async () => {
                setDraft(o.value);
                // Pills commit immediately — no blur.
                if (validate) {
                  const e = validate(o.value);
                  if (e) { setError(e); return; }
                }
                setSaving(true);
                try { await onSave(o.value); setEditing(false); }
                catch (err) { setError(err?.message || "Save failed"); }
                finally { setSaving(false); }
              }}
              style={{
                padding: "4px 12px", fontSize: 11, fontWeight: 600,
                borderRadius: 999, cursor: "pointer",
                background: active ? `${COLORS.accent}18` : "transparent",
                border: `1px solid ${active ? COLORS.accent : COLORS.border}`,
                color: active ? COLORS.accent : COLORS.textMuted,
                transition: "all 0.15s",
              }}
            >
              {o.label ?? o.value}
            </button>
          );
        })}
      </div>
    );
  } else if (variant === "custom" && renderInput) {
    input = renderInput(draft, setDraft, commit, revert);
  }

  return (
    <div
      style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "4px 0", ...style }}
      // Don't propagate clicks inside the input back to startEdit.
      onClick={(e) => e.stopPropagation()}
    >
      {label && (
        <div style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 600, minWidth: 100, paddingTop: 7 }}>
          {label}
        </div>
      )}
      <div style={{ flex: 1 }}>
        {input}
        {error && (
          <div style={{ fontSize: 11, color: COLORS.pink || "#ef4444", marginTop: 4 }}>
            {error}
          </div>
        )}
        {!error && hint && (
          <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 4 }}>
            {hint}
          </div>
        )}
        {/* Tiny help-text reminding the user of the keyboard shortcuts.
            Hidden on pill variant which commits on click. */}
        {variant !== "pill" && variant !== "slider" && (
          <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4, opacity: 0.7 }}>
            {saving ? "Saving…" : `${variant === "textarea" ? "⌘ Enter" : "Enter"} to save · Esc to cancel`}
          </div>
        )}
      </div>
    </div>
  );
}
